import { Prisma } from '@prisma/client';
import { Decimal } from '@snap-types/types/decimal';
import { z } from 'zod/v4';

const customDecimalSchema = z.custom<Decimal>((val) => val instanceof Decimal, {
  error: 'Invalid value',
});

const customPrismaDecimalSchema = z.custom<Prisma.Decimal>(
  (val) => val instanceof Prisma.Decimal,
  { error: 'Invalid value' },
);

export const decimal = z
  .union([customPrismaDecimalSchema, customDecimalSchema])
  // @NOTE(shawk): this is important to make testing easier
  .transform((value) => new Decimal(value));

export const decimalLike = z.union([
  customPrismaDecimalSchema,
  customDecimalSchema,
  z.string().min(1),
  z.number(),
]);

export const decimalLikeToDecimal = decimalLike.transform((value, ctx) => {
  try {
    const decimal = new Decimal(value);

    if (decimal.isNaN()) {
      throw new Error('Invalid decimal value');
    }

    return decimal;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (_e: unknown) {
    ctx.issues.push({
      code: 'custom',
      message: 'Not a valid number',
      input: value,
    });

    return z.NEVER;
  }
});

export const decimalToNumber = decimal.transform((value) => value.toNumber());

/**
 * The `unknownToDecimal` schema exists as a workaround, manipulating the
 * inferred input type in order to avoid an issue with type inference.
 *
 * Specifically when working with React Hook Form and Decimals, there is a
 * problem where some of RHF's helpers greedily distribute over (possibly) large
 * unions tracing all paths to leaf properties in the form data (i.e. the
 * fields). These unions are massively inflated because those type helpers
 * iterate over all of the properties on the Decimal type (class) even those
 * that are not actually fields. In cases with complex nested forms this can
 * exceed TypeScript's complexity limits and cause errors.
 */
export const unknownToDecimal = z.unknown().pipe(decimalLikeToDecimal);

export function decimalGt(value: Decimal.Value) {
  return (ctx: z.core.ParsePayload<Decimal>) => {
    if (ctx.value.lte(value)) {
      ctx.issues.push({
        code: 'custom',
        message: `Must be greater than ${value}`,
        input: ctx.value,
      });
    }
  };
}

export function decimalLt(value: Decimal.Value) {
  return (ctx: z.core.ParsePayload<Decimal>) => {
    if (ctx.value.gte(value)) {
      ctx.issues.push({
        code: 'custom',
        message: `Must be less than ${value}`,
        input: ctx.value,
      });
    }
  };
}

export function decimalGte(value: Decimal.Value) {
  return (ctx: z.core.ParsePayload<Decimal>) => {
    if (ctx.value.lt(value)) {
      ctx.issues.push({
        code: 'custom',
        message: `Must be greater than or equal to ${value}`,
        input: ctx.value,
      });
    }
  };
}

export function decimalLte(value: Decimal.Value) {
  return (ctx: z.core.ParsePayload<Decimal>) => {
    if (ctx.value.gt(value)) {
      ctx.issues.push({
        code: 'custom',
        message: `Must be less than or equal to ${value}`,
        input: ctx.value,
      });
    }
  };
}
